import { PDF_CONTENT_TYPE } from "@contract-manager/shared";
import { useRef, useState } from "react";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import type { MessageKey } from "../i18n/messages";
import { validatePdfFile } from "../upload/fileValidation";
import { runUpload, UploadNotCancelledError } from "../upload/runUpload";
import { Alert } from "./Alert";
import { FolderSelect, useFolders } from "./FolderSelect";
import { UploadAudience } from "./UploadAudience";

type Phase = "idle" | "requesting" | "uploading" | "done" | "error";

/** Null for 未分類, which has no name of its own to show. */
function destinationNameOf(
  folders: readonly { folderId: string; name: string }[],
  folderId: string,
): string | null {
  return folders.find((folder) => folder.folderId === folderId)?.name ?? null;
}

const BUSY_LABEL: Partial<Record<Phase, MessageKey>> = {
  requesting: "contracts.upload.requesting",
  uploading: "contracts.upload.uploading",
};

export function UploadPanel({
  api,
  onUploaded,
}: {
  api: ApiClient;
  onUploaded: () => void;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  // Chosen before the file, and kept afterwards: filing a batch of contracts
  // into one folder is the common case, and re-picking it every time would be
  // the kind of friction that gets everything dropped in 未分類 instead.
  const [folderId, setFolderId] = useState("");
  const { folders } = useFolders(api);

  const busy = phase === "requesting" || phase === "uploading";

  const upload = async (file: File) => {
    const rejection = validatePdfFile(file);
    if (rejection !== null) {
      setPhase("error");
      setMessage(t(rejection));
      return;
    }

    setMessage(null);
    try {
      await runUpload(
        api,
        file,
        setPhase,
        folderId === "" ? undefined : folderId,
      );

      setPhase("done");
      // The extractor runs off the S3 event, so the row will not be EXTRACTED
      // yet. Say so rather than letting the list look broken.
      setMessage(
        t("contracts.upload.done", {
          fileName: file.name,
          folder:
            destinationNameOf(folders, folderId) ?? t("contracts.unfiled"),
        }),
      );
      onUploaded();
    } catch (caught) {
      // The failure worth acting on is the upload's; the cancellation that
      // followed it only changes what is left in the ledger.
      const reason =
        caught instanceof UploadNotCancelledError ? caught.uploadError : caught;
      if (isSignedOutError(reason)) {
        auth.reportSignedOut();
        return;
      }
      setPhase("error");
      setMessage(
        caught instanceof UploadNotCancelledError
          ? t("contracts.upload.notCancelled", {
              reason: describeError(reason, t),
            })
          : describeError(caught, t),
      );
      // The ledger may have moved either way: a cancelled upload took its row
      // out, one that could not be cancelled left it in.
      onUploaded();
    }
  };

  const destination =
    destinationNameOf(folders, folderId) ?? t("contracts.unfiled");

  return (
    <section className="ds-rows --gap-small">
      <div className="ds-cols --middle --gap-small">
        <span className="ds-text --label">
          {t("contracts.upload.destination")}
        </span>
        <FolderSelect
          // Only the folders this person may file into. Being able to read a
          // folder does not mean being allowed to add to it.
          folders={folders.filter((folder) => folder.access.canUpload)}
          value={folderId}
          disabled={busy}
          label={t("contracts.upload.destination")}
          onChange={setFolderId}
        />
      </div>

      <UploadAudience api={api} folderId={folderId} />

      <button
        type="button"
        className={`app-dropzone${dragging ? " --active" : ""}`}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file && !busy) {
            void upload(file);
          }
        }}
      >
        <span
          className="ds-icon --icon-file-upload --large"
          aria-hidden="true"
        />
        <span>
          <span className="ds-text --medium-bold">
            {t("contracts.upload.dropTitle")}
          </span>
          <span className="ds-text --small --muted">
            {t("contracts.upload.dropHint", { folder: destination })}
          </span>
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={PDF_CONTENT_TYPE}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Reset so re-selecting the same file fires change again.
          event.target.value = "";
          if (file) {
            void upload(file);
          }
        }}
      />

      {busy && (
        // Not `--loading`: SDS hides a loading notice's text, and which step
        // the upload is on is the point of this box.
        <div className="ds-notice --info">
          <p>{BUSY_LABEL[phase] === undefined ? null : t(BUSY_LABEL[phase])}</p>
        </div>
      )}

      {message !== null && !busy && (
        <Alert tone={phase === "error" ? "failed" : "done"}>{message}</Alert>
      )}
    </section>
  );
}
